/* eslint-disable camelcase */
import React, {useState} from 'react';
import {CircularProgress, Grid, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';

import {getPageData} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';

import {APP} from 'src/styles/variables';
import {
  CardDiv,
  MainWrapperDiv,
  InfoDiv,
  SpaceSpan,
  LabelTypography,
  LoaderDiv
} from './TaskcardStyles';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';

import HelperUtils from '@shared/utils/HelperUtils';
import EncryptUtils from 'src/utils/EncryptUtils';
import FormConstants from '@shared/constants/FormConstants';
import AuthUtils from 'src/utils/AuthUtils';
import {useModuleContext} from '@context/ModuleContext';
import {useAppContext} from '@context/AppContext';
interface CardComponentProps {
  fields: any;
  data: any;
  columns_per_row: number;
  is_border_required?: boolean;
  is_space_needed_between_label_value?: boolean;
  is_colon_needed?: boolean;
  module?: any;
  sub_module?: any;
  sectionUIData?: any;
  selectedRowDetails?: any;
  is_bold_font?: any;
  component_type?: any;
  recordId?: string;
}

const TaskCardComponent = (props: CardComponentProps): React.ReactElement => {
  const {
    appState: {applicationName}
  } = useAppContext();
  const {
    fields,
    data,
    columns_per_row,
    component_type,
    is_border_required = true,
    is_space_needed_between_label_value = false,
    is_colon_needed = true,
    module,
    sub_module,
    sectionUIData,
    selectedRowDetails,
    // is_bold_font = true,
    recordId,
    ...rest
  } = props;
  const {enqueueSnackbar} = useSnackbar();
  const [cardData, setCardData]: any = React.useState({});
  const [loader, setLoader] = useState(false);
  const {moduleState} = useModuleContext();
  const {isBH, isBHReason}: any = moduleState;

  const init = async () => {
    try {
      if (
        module &&
        sub_module &&
        sectionUIData?.sql_data?.sql &&
        sectionUIData?.sql_data?.variables
      ) {
        setLoader(true);
        const payload = {
          sql_data: sectionUIData?.sql_data,
          user_id: selectedRowDetails?.row?.EmpNo,
          record_id: selectedRowDetails?.id || recordId || '',
          OrderID: selectedRowDetails?.row?.OrderID,
          OrderName: selectedRowDetails?.row?.OrderName,
          OrderDate: selectedRowDetails?.row?.OrderDate,
          legacyApp: selectedRowDetails?.row?.legacyApp,
          legacyMRN: selectedRowDetails?.row?.legacyMRN,
          item_vendor_id: selectedRowDetails?.row?.ItemVendorID,
          transaction_id: selectedRowDetails?.row?.TransactionID,
          invoice_id: selectedRowDetails?.row?.InvoiceID,
          employment_number: selectedRowDetails?.row?.EmpNo,
          pay_date: selectedRowDetails?.row?.PayDate,
          distribution_order: selectedRowDetails?.row?.DistributionOrder,
          is_active: selectedRowDetails?.row?.IsActive,
          priority: selectedRowDetails?.row?.Priority,
          is_dependent: selectedRowDetails?.row?.IsDependent,
          EffectDt: selectedRowDetails?.row?.EffectDt,
          audit_sub_module_identifier: selectedRowDetails?.row?.ID,
          patient_info_id: selectedRowDetails?.row?.PatientInformationID,
          prescription_no: selectedRowDetails?.row?.PrescriptionNumber,
          drug: selectedRowDetails?.row?.Drug,
          refill_no: selectedRowDetails?.row?.RefillNumber,
          audit_identifier: AuthUtils.getAuditIdentifier(),
          audit_identifier_type: AuthUtils.getAuditIdentifierType(),
          audit_legacy_app: AuthUtils.getAuditLegacyApp(),
          isBH_selected: sectionUIData?.sql_data?.ignoreIsBH ? 'ignore_isBH' : isBH,
          isBH_reason: isBHReason,
          application_name: applicationName
        };

        const encryptRequestData = EncryptUtils.encryptedData(payload);
        const res = await getPageData(applicationName, module, sub_module, {
          tk: encryptRequestData
        });
        setCardData(res?.data);
        setLoader(false);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setLoader(false);
    }
  };
  React.useEffect(() => {
    init();
    setCardData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionUIData?.sql_data?.sql]);

  React.useEffect(() => {
    if (
      sub_module === FormConstants.PAGE_SECTION_TYPES.DOCUMENTS ||
      props?.component_type === FormConstants.COMPONENT_TYPE.DOCUMENT
    ) {
      setCardData(data);
    }
    // init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <MainWrapperDiv>
      <LoaderDiv>{loader && <CircularProgress />}</LoaderDiv>
      <Grid container spacing={2}>
        {/* Task Details Card */}
        <Grid item md={6 / columns_per_row} xs={12}>
          <CardDiv
            {...rest}
            isBorderRequired={!loader && is_border_required}
            component_type={component_type}>
            <Typography
              color="primary"
              sx={{
                fontWeight: 'bold',
                margin: '0.25em 0 0.25em 0.25em',
                textAlign: 'left',
                lineHeight: 'inherit',
                color: `${APP.PALETTE.SUB_MODULE_COLOR.MAIN}`
              }}>
              {sectionUIData?.label}
            </Typography>
            {!loader &&
              fields
                ?.filter((mainItem: any) => mainItem.label !== 'CommentHistory')
                .map((mainItem: any, mainIndex: number) => (
                  <Grid
                    sx={{padding: '0.2em 0.4em !important'}}
                    item
                    md={12 / columns_per_row}
                    xs={12}
                    key={mainIndex}>
                    <InfoDiv>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                        <LabelTypography
                          sx={{
                            margin: '0 5px 0 0',
                            color: APP.PALETTE.CARDS.LABEL,
                            fontWeight: 'normal'
                          }}>
                          {mainItem?.label}
                          {cardData?.[0]?.[mainItem?.data_column] !==
                            'No Data Found.' && is_colon_needed
                            ? '  :    '
                            : ''}
                        </LabelTypography>
                        <Typography>
                          {is_space_needed_between_label_value ? (
                            <SpaceSpan />
                          ) : (
                            ''
                          )}
                        </Typography>
                        {mainItem.is_pre_formatter_required ? (
                          <pre
                            style={{
                              margin: '0',
                              color: APP.PALETTE.CARDS.VALUE,
                              fontWeight: 'bold',
                              whiteSpace: 'pre-line'
                            }}>
                            {cardData?.[0]?.[mainItem?.data_column]}
                          </pre>
                        ) : (
                          <Typography
                            sx={{
                              margin: '0',
                              color: APP.PALETTE.CARDS.VALUE,
                              fontWeight: 'bold',
                              wordWrap: 'break-word'
                            }}>
                            {HelperUtils.formatData(
                              cardData?.[0]?.[mainItem?.data_column],
                              mainItem?.type,
                              mainItem?.formatter || mainItem?.format
                            )}
                          </Typography>
                        )}
                      </div>
                    </InfoDiv>
                  </Grid>
                ))}
          </CardDiv>
        </Grid>

        {/* Comment History Card */}

        <Grid item md={6 / columns_per_row} xs={12}>
          <CardDiv
            {...rest}
            isBorderRequired={!loader && is_border_required}
            component_type={component_type}>
            <Typography
              color="primary"
              sx={{
                fontWeight: 'bold',
                margin: '0.25em 0 0.25em 0.25em',
                textAlign: 'left',
                lineHeight: 'inherit',
                color: `${APP.PALETTE.SUB_MODULE_COLOR.MAIN}`
              }}>
              Comment History
            </Typography>
            {!loader &&
              fields
                ?.filter((mainItem: any) => mainItem.label === 'CommentHistory')
                .map((mainItem: any, mainIndex: number) => (
                  <div
                    key={mainIndex}
                    style={{
                      maxHeight: '159px',
                      overflowY: 'auto'
                    }}>
                    {mainItem.is_pre_formatter_required ? (
                      <pre
                        style={{
                          margin: '0',
                          color: APP.PALETTE.CARDS.VALUE,
                          fontWeight: 'bold',
                          whiteSpace: 'pre-line'
                        }}>
                        {cardData?.[0]?.[mainItem?.data_column]}
                      </pre>
                    ) : (
                      <Typography
                        sx={{
                          margin: '0',
                          color: APP.PALETTE.CARDS.VALUE,
                          fontWeight: 'bold'
                        }}>
                        {HelperUtils.formatData(
                          cardData?.[0]?.[mainItem?.data_column],
                          mainItem?.type,
                          mainItem?.formatter || mainItem?.format
                        )}
                      </Typography>
                    )}
                  </div>
                ))}
          </CardDiv>
        </Grid>
      </Grid>
    </MainWrapperDiv>
  );
};

export default TaskCardComponent;
